import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save,
  Calendar,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Copy
} from 'lucide-react';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEFAULT_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const DraggableRecipe = ({ recipe, category, isUsed }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `recipe-${recipe.id}`,
    data: { recipe, category }
  });

  const categoryColors = {
    Daal: 'border-l-orange-500',
    Sabzi: 'border-l-green-500',
    Salad: 'border-l-cyan-500',
    Sweet: 'border-l-pink-500',
    Other: 'border-l-purple-500'
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`recipe-drag-item border-l-4 ${categoryColors[category] || 'border-l-purple-500'} ${isDragging ? 'opacity-50' : ''} ${isUsed ? 'bg-yellow-100 border-yellow-400' : ''}`}
      title={isUsed ? 'Used in rotation' : 'Not used yet'}
    >
      <span className="flex items-center gap-2">
        {isUsed && <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></span>}
        {recipe.recipe_name}
      </span>
    </div>
  );
};

const DroppableSlot = ({ day, week, slot, value, onClear, onDuplicate, isExtra, onRemoveExtra }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `${week}-${day}-${slot}`,
    data: { week, day, slot }
  });

  const slotLabels = {
    sabzi: 'Sabzi',
    daal: 'Daal',
    salad: 'Salad',
    extra: 'Extra'
  };

  const slotColors = {
    sabzi: 'text-green-700',
    daal: 'text-orange-700',
    salad: 'text-cyan-700',
    extra: 'text-purple-600'
  };

  return (
    <div
      ref={setNodeRef}
      className={`slot-container ${value ? 'filled' : ''} ${isOver ? 'drop-active' : ''}`}
    >
      {value ? (
        <div className="w-full flex items-center justify-between gap-1">
          <span className={`text-xs font-semibold ${slotColors[slot]} truncate flex-1`}>
            {value}
          </span>
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => onDuplicate(week, day, slot, value)}
              className="p-1 hover:bg-stone-200 rounded"
              title="Duplicate"
            >
              <Copy size={12} />
            </button>
            <button
              onClick={() => {
                onClear(week, day, slot);
                if (isExtra && onRemoveExtra) onRemoveExtra(week, day);
              }}
              className="p-1 hover:bg-red-100 text-red-500 rounded"
              title="Clear"
            >
              <XCircle size={12} />
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full flex items-center justify-between">
          <span className={`text-xs ${isExtra ? 'text-purple-400' : 'text-stone-400'}`}>{slotLabels[slot]}</span>
          {isExtra && onRemoveExtra && (
            <button
              onClick={() => onRemoveExtra(week, day)}
              className="p-0.5 hover:bg-red-100 text-red-400 rounded"
              title="Remove extra slot"
            >
              <XCircle size={10} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const RegularRotation = () => {
  const [recipes, setRecipes] = useState([]);
  const [rotations, setRotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usedRecipes, setUsedRecipes] = useState(new Set());

  const [rotationLength, setRotationLength] = useState(1);
  const [selectedWeekdays, setSelectedWeekdays] = useState(DEFAULT_WEEKDAYS);
  const [schedule, setSchedule] = useState({});
  const [rotationId, setRotationId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  
  // Track which days have extra slots enabled
  const [extraSlots, setExtraSlots] = useState({}); // {"Week1-Monday": true, "Week2-Friday": true}

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recipesRes, regularRotRes, specialRotRes] = await Promise.all([
        axios.get(`${API}/recipes`),
        axios.get(`${API}/rotations/regular`),
        axios.get(`${API}/rotations/special`)
      ]);
      setLoadFailed(false);
      
      const eligibleRecipes = recipesRes.data.filter(r => 
        r.visibility_tag === 'Regular Rotation Eligible' && r.active
      );
      setRecipes(eligibleRecipes);

      // Collect all used recipe names from both rotations
      const used = new Set();
      const extras = {};
      
      // Check regular rotation
      if (regularRotRes.data.length > 0) {
        const rotation = regularRotRes.data[0];
        setRotationId(rotation.id);
        setRotationLength(rotation.rotation_length);
        setSelectedWeekdays(rotation.weekdays || DEFAULT_WEEKDAYS);
        setSchedule(rotation.schedule || {});
        
        const schedule = rotation.schedule || {};
        Object.entries(schedule).forEach(([weekKey, week]) => {
          Object.entries(week).forEach(([dayKey, day]) => {
            if (day.sabzi) used.add(day.sabzi);
            if (day.daal) used.add(day.daal);
            if (day.salad) used.add(day.salad);
            if (day.extra) {
              used.add(day.extra);
              extras[`${weekKey}-${dayKey}`] = true;
            }
          });
        });
      } else {
        initializeSchedule(1, DEFAULT_WEEKDAYS);
      }
      
      setExtraSlots(extras);
      
      // Check Special rotation
      if (specialRotRes.data.length > 0) {
        const specialRot = specialRotRes.data[0];
        const specialSchedule = specialRot.schedule || {};
        Object.values(specialSchedule).forEach(week => {
          if (week.main) {
            used.add(week.main);
            if (week.roti) used.add(week.roti);
            if (week.raita) used.add(week.raita);
            if (week.side) used.add(week.side);
          } else {
            Object.values(week).forEach(day => {
              if (day.main) used.add(day.main);
              if (day.roti) used.add(day.roti);
              if (day.raita) used.add(day.raita);
              if (day.side) used.add(day.side);
            });
          }
        });
      }
      
      setUsedRecipes(used);
    } catch (error) {
      setLoadFailed(true);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const initializeSchedule = (length, weekdays) => {
    const newSchedule = {};
    for (let w = 1; w <= length; w++) {
      newSchedule[`Week${w}`] = {};
      weekdays.forEach(day => {
        newSchedule[`Week${w}`][day] = {
          sabzi: null,
          daal: null,
          salad: null,
          extra: null,
          directions: ''
        };
      });
    }
    setSchedule(newSchedule);
  };

  const handleRotationLengthChange = (length) => {
    const numLength = parseInt(length);
    setRotationLength(numLength);
    // Preserve existing schedule data when extending weeks
    const newSchedule = { ...schedule };
    for (let w = 1; w <= numLength; w++) {
      const weekKey = `Week${w}`;
      if (!newSchedule[weekKey]) {
        newSchedule[weekKey] = {};
        selectedWeekdays.forEach(day => {
          newSchedule[weekKey][day] = {
            sabzi: null,
            daal: null,
            salad: null,
            extra: null,
            directions: ''
          };
        });
      }
    }
    // Remove weeks beyond new length
    Object.keys(newSchedule).forEach(key => {
      const weekNum = parseInt(key.replace('Week', ''));
      if (weekNum > numLength) {
        delete newSchedule[key];
      }
    });
    setRotationLength(numLength);
    setSchedule(newSchedule);
  };

  const addExtraSlot = (week, day) => {
    setExtraSlots(prev => ({
      ...prev,
      [`${week}-${day}`]: true
    }));
  };

  const removeExtraSlot = (week, day) => {
    setExtraSlots(prev => {
      const updated = { ...prev };
      delete updated[`${week}-${day}`];
      return updated;
    });
    // Also clear the extra value in schedule
    setSchedule(prev => ({
      ...prev,
      [week]: {
        ...prev[week],
        [day]: {
          ...prev[week][day],
          extra: null
        }
      }
    }));
  };

  const handleWeekdayToggle = (day) => {
    let newWeekdays;
    if (selectedWeekdays.includes(day)) {
      // Remove day
      if (selectedWeekdays.length === 1) {
        toast.error('You must have at least one day selected');
        return;
      }
      newWeekdays = selectedWeekdays.filter(d => d !== day);
    } else {
      // Add day - maintain order
      newWeekdays = ALL_WEEKDAYS.filter(d => selectedWeekdays.includes(d) || d === day);
    }
    
    setSelectedWeekdays(newWeekdays);
    
    // Update schedule
    const newSchedule = { ...schedule };
    for (let w = 1; w <= rotationLength; w++) {
      const weekKey = `Week${w}`;
      if (!newSchedule[weekKey]) {
        newSchedule[weekKey] = {};
      }
      // Add new day if needed
      if (!selectedWeekdays.includes(day) && !newSchedule[weekKey][day]) {
        newSchedule[weekKey][day] = {
          sabzi: null,
          daal: null,
          salad: null,
          extra: null,
          directions: ''
        };
      }
      // Remove day if deselected
      if (selectedWeekdays.includes(day) && !newWeekdays.includes(day)) {
        delete newSchedule[weekKey][day];
      }
    }
    setSchedule(newSchedule);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const { recipe, category } = active.data.current;
    const [week, day, slot] = over.id.split('-').slice(0, 3);
    const targetSlot = over.id.split('-').pop();

    // Validate category matches slot (extra slot accepts any category)
    const slotCategoryMap = {
      sabzi: 'Sabzi',
      daal: 'Daal',
      salad: 'Salad'
    };

    // Extra slot accepts any recipe
    if (targetSlot !== 'extra' && slotCategoryMap[targetSlot] !== category) {
      toast.error(`Cannot drop ${category} in ${targetSlot} slot`);
      return;
    }

    setSchedule(prev => ({
      ...prev,
      [week]: {
        ...prev[week],
        [day]: {
          ...prev[week][day],
          [targetSlot]: recipe.recipe_name
        }
      }
    }));
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const clearSlot = (week, day, slot) => {
    setSchedule(prev => ({
      ...prev,
      [week]: {
        ...prev[week],
        [day]: {
          ...prev[week][day],
          [slot]: null
        }
      }
    }));
  };

  const duplicateSlot = (fromWeek, fromDay, slot, value) => {
    // For now, just show a toast - can implement a modal for selecting target days
    toast.info(`To duplicate "${value}", drag it from the recipe list`);
  };

  const handleSave = async () => {
    if (loadFailed) {
      toast.error('Could not load your existing rotation. Please reload the page before saving to avoid losing data.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: 'Regular Rotation',
        rotation_length: rotationLength,
        weekdays: selectedWeekdays,
        schedule
      };

      if (rotationId) {
        await axios.put(`${API}/rotations/regular/${rotationId}`, payload);
      } else {
        const response = await axios.post(`${API}/rotations/regular`, payload);
        setRotationId(response.data.id);
      }
      toast.success('Rotation saved successfully');
    } catch (error) {
      toast.error('Failed to save rotation');
    } finally {
      setSaving(false);
    }
  };

  const getDayStatus = (weekSchedule, day) => {
    if (!weekSchedule || !weekSchedule[day]) return 'incomplete';
    const slots = weekSchedule[day];
    // Extra is optional, so only count sabzi, daal, salad for completion
    const filled = [slots.sabzi, slots.daal, slots.salad].filter(Boolean).length;
    if (filled === 3) return 'complete';
    if (filled > 0) return 'partial';
    return 'incomplete';
  };

  const getWeekCompletion = (weekKey) => {
    if (!schedule[weekKey]) return { complete: 0, total: selectedWeekdays.length };
    let complete = 0;
    selectedWeekdays.forEach(day => {
      if (getDayStatus(schedule[weekKey], day) === 'complete') complete++;
    });
    return { complete, total: selectedWeekdays.length };
  };

  const getRecipeFrequency = () => {
    const freq = {};
    Object.values(schedule).forEach(week => {
      Object.values(week).forEach(day => {
        [day.sabzi, day.daal, day.salad, day.extra].forEach(recipe => {
          if (recipe) {
            freq[recipe] = (freq[recipe] || 0) + 1;
          }
        });
      });
    });
    return freq;
  };

  const daalRecipes = recipes.filter(r => r.category === 'Daal');
  const sabziRecipes = recipes.filter(r => r.category === 'Sabzi');
  const saladRecipes = recipes.filter(r => r.category === 'Salad');
  // Extra recipes: anything that's not Daal, Sabzi, or Salad (e.g., Sweet, Other)
  const extraRecipes = recipes.filter(r => !['Daal', 'Sabzi', 'Salad'].includes(r.category));
  const frequency = getRecipeFrequency();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6" data-testid="regular-rotation-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight" >
              Regular Rotation
            </h1>
            <p className="text-stone-500 mt-1">Weekly menu planning</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
            data-testid="save-rotation-btn"
          >
            {saving ? <span className="spinner" /> : <Save size={20} />}
            <span>Save Rotation</span>
          </button>
        </div>

        {/* Settings */}
        <div className="virsa-card p-4">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-stone-600 mb-2">Rotation Length</label>
                <Select value={rotationLength.toString()} onValueChange={handleRotationLengthChange}>
                  <SelectTrigger className="h-12" data-testid="rotation-length-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Week</SelectItem>
                    <SelectItem value="2">2 Weeks</SelectItem>
                    <SelectItem value="3">3 Weeks</SelectItem>
                    <SelectItem value="4">4 Weeks</SelectItem>
                    <SelectItem value="5">5 Weeks</SelectItem>
                    <SelectItem value="6">6 Weeks</SelectItem>
                    <SelectItem value="7">7 Weeks</SelectItem>
                    <SelectItem value="8">8 Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Day Selection */}
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-3">Active Days</label>
              <div className="flex flex-wrap gap-3">
                {ALL_WEEKDAYS.map(day => {
                  const isSelected = selectedWeekdays.includes(day);
                  return (
                    <button
                      type="button"
                      key={day} 
                      onClick={() => handleWeekdayToggle(day)}
                      style={isSelected ? { backgroundColor: '#124242', borderColor: '#124242', color: 'white' } : {}}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all font-semibold ${
                        isSelected 
                          ? '' 
                          : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                      }`}
                      data-testid={`day-toggle-${day.toLowerCase()}`}
                    >
                      <div 
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-white bg-white' 
                            : 'border-stone-300 bg-white'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3" style={{ color: '#124242' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium">{day.slice(0, 3)}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-stone-400 mt-2">
                {selectedWeekdays.length} day(s) selected
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Recipe Lists */}
          <div className="space-y-4">
            {/* Legend for yellow indicator */}
            <div className="text-xs text-stone-500 flex items-center gap-2 px-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span>Used in rotation</span>
            </div>
            
            <div className="virsa-card p-4">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                Daal ({daalRecipes.length})
              </h3>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {daalRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="Daal" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
              </div>
            </div>

            <div className="virsa-card p-4">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                Sabzi ({sabziRecipes.length})
              </h3>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {sabziRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="Sabzi" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
              </div>
            </div>

            <div className="virsa-card p-4">
              <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                Salad ({saladRecipes.length})
              </h3>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {saladRecipes.map(recipe => (
                  <DraggableRecipe 
                    key={recipe.id} 
                    recipe={recipe} 
                    category="Salad" 
                    isUsed={usedRecipes.has(recipe.recipe_name)}
                  />
                ))}
              </div>
            </div>

            {extraRecipes.length > 0 && (
              <div className="virsa-card p-4 border-2 border-purple-200 bg-purple-50/30">
                <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  Extra ({extraRecipes.length})
                  <span className="text-xs text-purple-500 font-normal ml-1">(For special days)</span>
                </h3>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {extraRecipes.map(recipe => (
                    <DraggableRecipe 
                      key={recipe.id} 
                      recipe={recipe} 
                      category={recipe.category} 
                      isUsed={usedRecipes.has(recipe.recipe_name)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Frequency Summary */}
            {Object.keys(frequency).length > 0 && (
              <div className="virsa-card p-4">
                <h3 className="font-bold text-stone-800 mb-3">Recipe Frequency</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {Object.entries(frequency).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                    <div key={name} className="flex justify-between text-sm">
                      <span className="truncate flex-1">{name}</span>
                      <span className="badge badge-teal ml-2">{count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Schedule Grid */}
          <div className="lg:col-span-3 space-y-6">
            {Array.from({ length: rotationLength }, (_, i) => i + 1).map(weekNum => {
              const weekKey = `Week${weekNum}`;
              const completion = getWeekCompletion(weekKey);
              
              return (
                <div key={weekKey} className="virsa-card p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-stone-800" >
                      Week {weekNum}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className={`badge ${completion.complete === completion.total ? 'badge-success' : 'badge-warning'}`}>
                        {completion.complete}/{completion.total} days
                      </span>
                    </div>
                  </div>

                  <div className="rotation-grid">
                    {selectedWeekdays.map(day => {
                      const status = getDayStatus(schedule[weekKey], day);
                      const statusIcon = {
                        complete: <CheckCircle size={14} className="text-green-600" />,
                        partial: <AlertCircle size={14} className="text-orange-500" />,
                        incomplete: <XCircle size={14} className="text-red-500" />
                      };

                      return (
                        <div
                          key={day}
                          className={`day-card ${status === 'complete' ? 'border-green-400' : status === 'partial' ? 'border-orange-400' : ''}`}
                        >
                          <div className="day-card-header flex items-center justify-between">
                            <span>{day}</span>
                            {statusIcon[status]}
                          </div>
                          <div className="day-card-body space-y-2">
                            <DroppableSlot
                              week={weekKey}
                              day={day}
                              slot="sabzi"
                              value={schedule[weekKey]?.[day]?.sabzi}
                              onClear={clearSlot}
                              onDuplicate={duplicateSlot}
                            />
                            <DroppableSlot
                              week={weekKey}
                              day={day}
                              slot="daal"
                              value={schedule[weekKey]?.[day]?.daal}
                              onClear={clearSlot}
                              onDuplicate={duplicateSlot}
                            />
                            <DroppableSlot
                              week={weekKey}
                              day={day}
                              slot="salad"
                              value={schedule[weekKey]?.[day]?.salad}
                              onClear={clearSlot}
                              onDuplicate={duplicateSlot}
                            />
                            {/* Extra slot - only show if enabled for this day */}
                            {extraSlots[`${weekKey}-${day}`] && (
                              <div className="mt-2">
                                <DroppableSlot
                                  week={weekKey}
                                  day={day}
                                  slot="extra"
                                  value={schedule[weekKey]?.[day]?.extra}
                                  onClear={clearSlot}
                                  onDuplicate={duplicateSlot}
                                  isExtra={true}
                                  onRemoveExtra={removeExtraSlot}
                                />
                              </div>
                            )}
                            {/* Add Extra button */}
                            {!extraSlots[`${weekKey}-${day}`] && (
                              <button
                                onClick={() => addExtraSlot(weekKey, day)}
                                className="w-full flex items-center justify-center gap-1 py-1 mt-1 text-xs text-stone-400 hover:text-purple-500 hover:bg-purple-50 rounded transition-colors"
                                title="Add extra item for this day"
                              >
                                <Plus size={10} />
                                <span>Extra</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DndContext>
  );
};
